// Drag-to-scroll for horizontal swipe rows.
//
// Touch devices scroll these rows natively, and we hide the scrollbar for a
// clean "swipe" feel — which leaves MOUSE users with no way to move the row.
// This hook adds click-and-drag panning plus vertical-wheel → horizontal
// scrolling, and swallows the click that ends a real drag so cards don't
// navigate when the user was only scrolling.
//
// Usage:
//   const drag = useDragScroll();
//   <div {...drag.props} className="no-scrollbar" style={{ overflowX: "auto" }}>
import { useRef, useEffect, useCallback } from "react";

const DRAG_THRESHOLD = 4; // px before a press counts as a drag, not a click

export default function useDragScroll() {
    const ref = useRef(null);
    const st = useRef({ down: false, startX: 0, startLeft: 0, moved: false });

    // Wheel: map vertical wheel to horizontal scroll. Registered natively so
    // preventDefault works (React's synthetic wheel handler is passive).
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const onWheel = (e) => {
            if (e.deltaX !== 0) return;                       // real horizontal wheel — let it through
            if (el.scrollWidth <= el.clientWidth) return;     // nothing to scroll
            e.preventDefault();
            el.scrollLeft += e.deltaY;
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, []);

    // Window-level move/up so the drag survives the pointer leaving the row.
    useEffect(() => {
        const onMove = (e) => {
            const el = ref.current, s = st.current;
            if (!s.down || !el) return;
            const dx = e.clientX - s.startX;
            if (!s.moved && Math.abs(dx) > DRAG_THRESHOLD) s.moved = true;
            if (s.moved) {
                el.scrollLeft = s.startLeft - dx;
                e.preventDefault();
            }
        };
        const onUp = () => {
            const el = ref.current, s = st.current;
            if (!s.down) return;
            s.down = false;
            if (el) el.style.cursor = "grab";
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
        };
    }, []);

    const onPointerDown = useCallback((e) => {
        if (e.pointerType === "touch") return;   // native touch scrolling already works
        const el = ref.current;
        if (!el || el.scrollWidth <= el.clientWidth) return;
        st.current = { down: true, startX: e.clientX, startLeft: el.scrollLeft, moved: false };
        el.style.cursor = "grabbing";
    }, []);

    // Capture phase: kill the click that ends a drag before cards see it.
    const onClickCapture = useCallback((e) => {
        if (st.current.moved) {
            e.preventDefault();
            e.stopPropagation();
            st.current.moved = false;
        }
    }, []);

    // Native drag (images/links) fights pointer panning — turn it off.
    const onDragStart = useCallback((e) => e.preventDefault(), []);

    // `handlers` is spread onto the row; `ref` is set separately so the
    // caller keeps full control of its own style object.
    return { ref, handlers: { onPointerDown, onClickCapture, onDragStart } };
}
